import { useEffect } from 'react'
import { createBrowserRouter, RouterProvider, createRoutesFromElements, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { useAuthStore } from '@/store/authStore'
import { LoginPage } from '@/pages/auth/LoginPage'
import { SignUpPage } from '@/pages/auth/SignUpPage'
import { OnboardingPage } from '@/pages/auth/OnboardingPage'
import { DashboardLayout } from '@/pages/dashboard/DashboardLayout'
import { OverviewPage } from '@/pages/dashboard/OverviewPage'
import { SitesPage } from '@/pages/dashboard/SitesPage'
import { PagesPage } from '@/pages/dashboard/PagesPage'
import { EditorPage } from '@/pages/dashboard/EditorPage'
import { SettingsPage } from '@/pages/dashboard/SettingsPage'
import { ApiKeysPage } from '@/pages/settings/ApiKeysPage'
import { BillingPage } from '@/pages/dashboard/BillingPage'
import { InvoicesPage } from '@/pages/dashboard/InvoicesPage'
import { NotificationsPage } from '@/pages/dashboard/NotificationsPage'
import { BlogAdminPage } from '@/pages/dashboard/BlogAdminPage'
import { BlogRedirectPage } from '@/pages/dashboard/BlogRedirectPage'
import { NewBlogPostPage, EditBlogPostPage } from '@/pages/dashboard/BlogPostEditorPage'
import { AnalyticsPage } from '@/pages/dashboard/AnalyticsPage'
import { TeamPage } from '@/pages/dashboard/TeamPage'
import { AuthGuard } from '@/components/auth/AuthGuard'
import { TenantGuard } from '@/components/auth/TenantGuard'
import { RoleGuard } from '@/components/auth/RoleGuard'
import { PlatformAdminGuard } from '@/components/auth/PlatformAdminGuard'
import { InvitePage } from '@/pages/auth/InvitePage'
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage'
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage'
import { PlatformAdminLayout } from '@/pages/platform-admin/PlatformAdminLayout'
import { PlatformAdminPage } from '@/pages/platform-admin/PlatformAdminPage'
import { PlatformAdminTenantDetailPage } from '@/pages/platform-admin/PlatformAdminTenantDetailPage'

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Onboarding — authenticated but no tenant yet */}
      <Route path="/onboarding" element={<AuthGuard><OnboardingPage /></AuthGuard>} />

      {/* Protected dashboard */}
      <Route path="/" element={<AuthGuard><TenantGuard><DashboardLayout /></TenantGuard></AuthGuard>}>
        <Route index element={<OverviewPage />} />
        <Route path="sites" element={<RoleGuard require="canAccessSites"><SitesPage /></RoleGuard>} />
        <Route path="sites/:siteId/pages" element={<RoleGuard require="canAccessSites"><PagesPage /></RoleGuard>} />
        <Route path="sites/:siteId/blog" element={<RoleGuard require="canAccessBlog"><BlogAdminPage /></RoleGuard>} />
        <Route path="sites/:siteId/analytics" element={<RoleGuard require="canAccessSites"><AnalyticsPage /></RoleGuard>} />
        <Route path="blog" element={<RoleGuard require="canAccessBlog"><BlogRedirectPage /></RoleGuard>} />
        <Route path="team" element={<RoleGuard require="canAccessTeam"><TeamPage /></RoleGuard>} />
        <Route path="settings" element={<RoleGuard require="canAccessSettings"><SettingsPage /></RoleGuard>} />
        <Route path="settings/api-keys" element={<RoleGuard require="canAccessSettings"><ApiKeysPage /></RoleGuard>} />
        <Route path="billing" element={<RoleGuard require="canAccessBilling"><BillingPage /></RoleGuard>} />
        <Route path="billing/invoices" element={<RoleGuard require="canAccessBilling"><InvoicesPage /></RoleGuard>} />
        <Route path="notifications" element={<NotificationsPage />} />
      </Route>

      {/* Editor — full screen, outside dashboard layout */}
      <Route path="/editor/:pageId" element={<AuthGuard><TenantGuard><RoleGuard require="canAccessSites"><EditorPage /></RoleGuard></TenantGuard></AuthGuard>} />

      {/* Blog post editor — full screen */}
      <Route path="/sites/:siteId/blog/new" element={<AuthGuard><TenantGuard><RoleGuard require="canAccessBlog"><NewBlogPostPage /></RoleGuard></TenantGuard></AuthGuard>} />
      <Route path="/sites/:siteId/blog/:postId/edit" element={<AuthGuard><TenantGuard><RoleGuard require="canAccessBlog"><EditBlogPostPage /></RoleGuard></TenantGuard></AuthGuard>} />

      {/* Platform admin — cross-tenant, NOT nested under TenantGuard (founder may or may not own a tenant) */}
      <Route path="/platform-admin" element={<AuthGuard><PlatformAdminGuard><PlatformAdminLayout /></PlatformAdminGuard></AuthGuard>}>
        <Route index element={<PlatformAdminPage />} />
        <Route path="tenants/:tenantId" element={<PlatformAdminTenantDetailPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </>
  )
)

export function App() {
  const initialize = useAuthStore((s) => s.initialize)

  useEffect(() => {
    void initialize()
  }, [initialize])

  return (
    <>
      <Toaster richColors position="top-right" />
      <RouterProvider router={router} />
    </>
  )
}
